import React, { useState } from 'react';
import { Paper, Box, Avatar, Typography, IconButton, Dialog } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

const LinkedInPreview = ({ 
  children, 
  user, 
  sx = {}, 
  postImage, 
  onRemoveImage,
  carouselFile,
  onRemoveCarousel
}) => {
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [showCarouselPopup, setShowCarouselPopup] = useState(false);
  
  console.log('LinkedInPreview received user:', user);

  const profileImage = user?.user?.image || user?.image || '/default-avatar.png';
  const userName = user?.user?.name || user?.name || user?.username;
  const userHeadline = user?.user?.headline || user?.headline || '';

  console.log('Processed user data:', {
    profileImage,
    userName,
    userHeadline
  });

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...sx
      }}
    >
      <Box 
        className="preview-content"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Author Info - Static Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Avatar
            src={profileImage}
            alt={userName}
            sx={{ 
              width: 48, 
              height: 48,
              border: '2px solid',
              borderColor: 'primary.light'
            }}
            >
              {userName?.[0]}
            </Avatar>
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                color: '#000',
                fontSize: '14px',
                lineHeight: 1.4
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '12px',
                lineHeight: 1.4
              }}
            >
              {userHeadline}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '12px',
                mt: 0.5,
                display: 'block'
              }}
            >
              {new Date().toLocaleString('en-US', { 
                hour: 'numeric',
                minute: 'numeric',
                hour12: true 
              })} • Just now
            </Typography>
          </Box>
        </Box>

          {/* Attachments Section */}
          {(postImage || carouselFile) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Image thumbnail */}
              {postImage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '12px'
                    }}
                  >
                    Attached image
                  </Typography>
                  <Box 
                    sx={{ 
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.1)',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover .preview-overlay': {
                        opacity: 1
                      }
                    }}
                    onClick={() => setShowImagePopup(true)}
                  >
                    <img 
                      src={postImage} 
                      alt="Attached" 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Preview Overlay */}
                    <Box
                      className="preview-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      }}
                    >
                      <ZoomInIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    {onRemoveImage && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveImage();
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': {
                            backgroundColor: 'white',
                            transform: 'scale(1.1)'
                          },
                          zIndex: 2
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}

              {/* Carousel thumbnail */}
              {carouselFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '12px'
                    }}
                  >
                    Attached carousel
                  </Typography>
                  <Box 
                    sx={{ 
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.1)',
                      position: 'relative',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      '&:hover .preview-overlay': {
                        opacity: 1
                      }
                    }}
                    onClick={() => setShowCarouselPopup(true)}
                  >
                    <ViewCarouselIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    {/* Preview Overlay */}
                    <Box
                      className="preview-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      }}
                    >
                      <OpenInFullIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    {onRemoveCarousel && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveCarousel();
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': {
                            backgroundColor: 'white',
                            transform: 'scale(1.1)'
                          },
                          zIndex: 2
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Post Content - Scrollable */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          p: 2
        }}>
          {children}
        </Box>
      </Box>

      {/* Image Popup Dialog */}
      <Dialog
        open={showImagePopup}
        onClose={() => setShowImagePopup(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: 'relative' }}>
          <img 
            src={postImage} 
            alt="Preview" 
            style={{
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
          <IconButton
            onClick={() => setShowImagePopup(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>

      {/* Carousel Popup Dialog */}
      <Dialog
        open={showCarouselPopup}
        onClose={() => setShowCarouselPopup(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ position: 'relative', p: 2 }}>
          <iframe
            src={carouselFile instanceof Blob ? URL.createObjectURL(carouselFile) : carouselFile}
            style={{
              width: '100%',
              height: '80vh',
              border: 'none'
            }}
            title="Carousel Preview"
          />
          <IconButton
            onClick={() => setShowCarouselPopup(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default LinkedInPreview; 